import { useState, useEffect } from 'react'
import { supabase } from '../services/supabase'
import * as XLSX from 'xlsx'
import { 
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer 
} from 'recharts'
import { 
  TrendingUp, TrendingDown, AlertTriangle, Download, 
  Package, Users, AlertCircle
} from 'lucide-react'
import ResumenDelDia from '../components/ResumenDelDia'
import PrediccionDiaria from '../components/PrediccionDiaria'
import DetalleProductoModal from '../components/DetalleProductoModal'

export default function Informes() {
  const [clientes, setClientes] = useState([])
  const [clienteSeleccionado, setClienteSeleccionado] = useState('todos')
  const [periodo, setPeriodo] = useState('mes')
  const [loading, setLoading] = useState(true)
  
  const [deliveriesRaw, setDeliveriesRaw] = useState([]) // ⭐ NUEVO: Para pasar a componentes
  const [datosEvolucion, setDatosEvolucion] = useState([])
  const [datosProductos, setDatosProductos] = useState([])
  const [datosDiaSemana, setDatosDiaSemana] = useState([])
  const [rankingClientes, setRankingClientes] = useState([])
  const [kpis, setKpis] = useState({
    ventasBrutas: 0,
    devoluciones: 0,
    ventasNetas: 0,
    porcentajeDevolucion: 0
  })
  const [comparativas, setComparativas] = useState({
    semana: { actual: 0, anterior: 0, cambio: 0 },
    mes: { actual: 0, anterior: 0, cambio: 0 },
    año: { actual: 0, anterior: 0, cambio: 0 }
  })
  const [sobrasPorCliente, setSobrasPorCliente] = useState([])
  const [alertas, setAlertas] = useState([])
  const [clientPricesMap, setClientPricesMap] = useState({})

  // Modal detalle (para productos, ranking, etc.)
  const [modalDetalle, setModalDetalle] = useState(null)

  useEffect(() => {
    cargarClientes()
  }, [])

  useEffect(() => {
    if (clientes.length > 0) {
      procesarDatos()
    }
  }, [clienteSeleccionado, periodo, clientes])

  async function cargarClientes() {
    try {
      const { data } = await supabase
        .from('clients')
        .select('id, alias, razon_social')
        .eq('activo', true)
        .order('alias')
      
      setClientes(data || [])
    } catch (error) {
      console.error('Error cargando clientes:', error)
    }
  }

  async function procesarDatos() {
    setLoading(true)
    
    try {
      const fechaFin = new Date()
      const fechaInicio = new Date()
      
      switch (periodo) {
        case 'semana':
          fechaInicio.setDate(fechaFin.getDate() - 7)
          break
        case 'mes':
          fechaInicio.setMonth(fechaFin.getMonth() - 1)
          break
        case 'año':
          fechaInicio.setFullYear(fechaFin.getFullYear() - 1)
          break
        case 'max':
          fechaInicio.setFullYear(2020, 0, 1)
          break
      }

      let query = supabase
        .from('deliveries')
        .select(`
          id,
          fecha,
          client_id,
          delivery_lines (
            id,
            product_id,
            cantidad_entregada,
            cantidad_devuelta,
            precio_unit,
            descuento_aplicable,
            products (nombre)
          ),
          clients (alias, razon_social, descuento_porcentaje)
        `)
        .gte('fecha', fechaInicio.toISOString().split('T')[0])
        .lte('fecha', fechaFin.toISOString().split('T')[0])
        .order('fecha', { ascending: true })

      if (clienteSeleccionado !== 'todos') {
        query = query.eq('client_id', clienteSeleccionado)
      }

      const { data: deliveries, error } = await query
      if (error) throw error

      // ⭐ GUARDAR deliveries raw para pasar a componentes
      setDeliveriesRaw(deliveries || [])

      // Cargar precios especiales actuales de los clientes
      const clientIds = [...new Set((deliveries || []).map(d => d.client_id))]
      let cpMap = {}
      if (clientIds.length > 0) {
        const { data: cpData } = await supabase
          .from('client_prices')
          .select('client_id, product_id, pvp')
          .in('client_id', clientIds)
          .eq('activo', true)
        ;(cpData || []).forEach(cp => {
          if (!cpMap[cp.client_id]) cpMap[cp.client_id] = {}
          cpMap[cp.client_id][cp.product_id] = Number(cp.pvp)
        })
      }
      setClientPricesMap(cpMap)

      // Procesar datos principales
      const resultado = procesarDeliveries(deliveries, cpMap)
      
      setDatosEvolucion(resultado.evolucion)
      setDatosProductos(resultado.productos)
      setDatosDiaSemana(resultado.diasSemana)
      setRankingClientes(resultado.ranking)
      setKpis(resultado.kpis)
      
      // Calcular comparativas
      await calcularComparativas(deliveries)
      
      // Generar análisis de sobras por cliente
      generarAnalisisSobrasPorCliente(deliveries)
      
      // Generar alertas
      generarAlertas(resultado.productos, resultado.diasSemana, resultado.ranking)

    } catch (error) {
      console.error('Error procesando datos:', error)
      alert('Error al procesar informes: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  // Devuelve el precio con descuento aplicado si corresponde
  function calcularPrecioCliente(clientId, productId, precioUnit, descuentoAplicable, descuentoPct, cpMap) {
    const precio = cpMap[clientId]?.[productId] ?? precioUnit
    return descuentoAplicable ? precio * (1 - descuentoPct / 100) : precio
  }

  function procesarDeliveries(deliveries, clientPricesMap = {}) {
    const evolucionPorDia = {}
    const productoStats = {}
    const clienteStats = {}
    const diaSemanaStats = {
      lunes: { entregas: 0, devoluciones: 0 },
      martes: { entregas: 0, devoluciones: 0 },
      miercoles: { entregas: 0, devoluciones: 0 },
      jueves: { entregas: 0, devoluciones: 0 },
      viernes: { entregas: 0, devoluciones: 0 },
      sabado: { entregas: 0, devoluciones: 0 },
      domingo: { entregas: 0, devoluciones: 0 }
    }

    let totalEntregas = 0
    let totalDevoluciones = 0

    deliveries?.forEach(delivery => {
      const fecha = delivery.fecha
      const fechaObj = new Date(fecha + 'T00:00:00')
      const clienteNombre = delivery.clients?.alias || delivery.clients?.razon_social || 'Desconocido'
      const descuentoPct = Number(delivery.clients?.descuento_porcentaje || 0)
      const clientId = delivery.client_id

      const diasSemana = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado']
      const diaSemana = diasSemana[fechaObj.getDay()]

      delivery.delivery_lines?.forEach(line => {
        const cantidad = line.cantidad_entregada || 0
        const devolucion = line.cantidad_devuelta || 0
        const cantidadNeta = cantidad - devolucion
        const productoNombre = line.products?.nombre || 'Desconocido'

        // Precio con descuento del cliente aplicado si corresponde
        const precioCliente = calcularPrecioCliente(
          clientId, line.product_id, line.precio_unit || 0,
          Boolean(line.descuento_aplicable), descuentoPct, clientPricesMap
        )

        const importeEntrega    = cantidad     * precioCliente
        const importeDevolucion = devolucion   * precioCliente
        const importeNeto       = cantidadNeta * precioCliente

        const fechaDevolucion = new Date(fechaObj)
        fechaDevolucion.setDate(fechaDevolucion.getDate() - 1)
        const fechaDevStr = fechaDevolucion.toISOString().split('T')[0]

        if (!evolucionPorDia[fecha]) {
          evolucionPorDia[fecha] = { fecha, entregas: 0, devoluciones: 0, neto: 0 }
        }
        evolucionPorDia[fecha].entregas += importeEntrega

        if (devolucion > 0) {
          if (!evolucionPorDia[fechaDevStr]) {
            evolucionPorDia[fechaDevStr] = { fecha: fechaDevStr, entregas: 0, devoluciones: 0, neto: 0 }
          }
          evolucionPorDia[fechaDevStr].devoluciones += importeDevolucion
        }

        if (!productoStats[productoNombre]) {
          productoStats[productoNombre] = {
            nombre: productoNombre,
            entregado: 0,
            devuelto: 0,
            porcentaje: 0,
            diasEntrega: new Set(),
            clientes: []
          }
        }
        productoStats[productoNombre].entregado += cantidad
        productoStats[productoNombre].devuelto += devolucion
        productoStats[productoNombre].diasEntrega.add(diaSemana)
        productoStats[productoNombre].clientes.push({
          nombre: clienteNombre,
          entregado: cantidad,
          devuelto: devolucion,
          porcentajeDev: cantidad > 0 ? ((devolucion / cantidad) * 100).toFixed(1) : '0.0'
        })

        if (!clienteStats[clienteNombre]) {
          clienteStats[clienteNombre] = {
            nombre: clienteNombre,
            ventas: 0,       // importe neto (lo que se cobra)
            ventasBruto: 0,  // importe bruto (para %Dev)
            devoluciones: 0,
            porcentajeDev: 0
          }
        }
        clienteStats[clienteNombre].ventas      += importeNeto
        clienteStats[clienteNombre].ventasBruto += importeEntrega
        clienteStats[clienteNombre].devoluciones += importeDevolucion

        if (diaSemanaStats[diaSemana]) {
          diaSemanaStats[diaSemana].entregas += importeEntrega
          if (devolucion > 0) {
            const diaAnteriorIdx = (fechaObj.getDay() - 1 + 7) % 7
            const diaAnterior = diasSemana[diaAnteriorIdx]
            if (diaSemanaStats[diaAnterior]) {
              diaSemanaStats[diaAnterior].devoluciones += importeDevolucion
            }
          }
        }

        totalEntregas += importeEntrega
        totalDevoluciones += importeDevolucion
      })
    })

    const evolucionArray = Object.values(evolucionPorDia)
      .map(d => ({ ...d, neto: d.entregas - d.devoluciones }))
      .sort((a, b) => new Date(a.fecha) - new Date(b.fecha))

    const productosArray = Object.values(productoStats)
      .map(p => ({
        ...p,
        porcentaje: p.entregado > 0 ? ((p.devuelto / p.entregado) * 100).toFixed(1) : 0,
        diasEntrega: Array.from(p.diasEntrega)
      }))
      .sort((a, b) => parseFloat(b.porcentaje) - parseFloat(a.porcentaje))

    const clientesArray = Object.values(clienteStats)
      .map(c => ({
        ...c,
        porcentajeDev: c.ventasBruto > 0 ? ((c.devoluciones / c.ventasBruto) * 100).toFixed(1) : 0
      }))
      .sort((a, b) => b.ventas - a.ventas)

    const diasSemanaArray = Object.entries(diaSemanaStats).map(([dia, stats]) => ({
      dia: dia.charAt(0).toUpperCase() + dia.slice(1),
      entregas: stats.entregas,
      devoluciones: stats.devoluciones,
      neto: stats.entregas - stats.devoluciones,
      porcentajeDev: stats.entregas > 0 ? ((stats.devoluciones / stats.entregas) * 100).toFixed(1) : 0
    }))

    return {
      evolucion: evolucionArray,
      productos: productosArray,
      diasSemana: diasSemanaArray,
      ranking: clientesArray,
      kpis: {
        ventasBrutas: totalEntregas,
        devoluciones: totalDevoluciones,
        ventasNetas: totalEntregas - totalDevoluciones,
        porcentajeDevolucion: totalEntregas > 0 ? ((totalDevoluciones / totalEntregas) * 100).toFixed(1) : 0
      }
    }
  }

  async function calcularComparativas(deliveriesActuales) {
    try {
      const hoy = new Date()
      
      const inicioSemanaAnterior = new Date(hoy)
      inicioSemanaAnterior.setDate(hoy.getDate() - 14)
      const finSemanaAnterior = new Date(hoy)
      finSemanaAnterior.setDate(hoy.getDate() - 7)
      
      const inicioMesAnterior = new Date(hoy)
      inicioMesAnterior.setMonth(hoy.getMonth() - 2)
      const finMesAnterior = new Date(hoy)
      finMesAnterior.setMonth(hoy.getMonth() - 1)
      
      const inicioAñoAnterior = new Date(hoy)
      inicioAñoAnterior.setFullYear(hoy.getFullYear() - 1)
      inicioAñoAnterior.setMonth(hoy.getMonth() - 1)
      const finAñoAnterior = new Date(hoy)
      finAñoAnterior.setFullYear(hoy.getFullYear() - 1)

      const ventasActuales = deliveriesActuales?.reduce((sum, d) => {
        return sum + (d.delivery_lines?.reduce((s, l) => 
          s + (l.cantidad_entregada * l.precio_unit), 0) || 0)
      }, 0) || 0

      const { data: semanaAnt } = await supabase
        .from('deliveries')
        .select('delivery_lines(cantidad_entregada, precio_unit)')
        .gte('fecha', inicioSemanaAnterior.toISOString().split('T')[0])
        .lte('fecha', finSemanaAnterior.toISOString().split('T')[0])

      const { data: mesAnt } = await supabase
        .from('deliveries')
        .select('delivery_lines(cantidad_entregada, precio_unit)')
        .gte('fecha', inicioMesAnterior.toISOString().split('T')[0])
        .lte('fecha', finMesAnterior.toISOString().split('T')[0])

      const { data: añoAnt } = await supabase
        .from('deliveries')
        .select('delivery_lines(cantidad_entregada, precio_unit)')
        .gte('fecha', inicioAñoAnterior.toISOString().split('T')[0])
        .lte('fecha', finAñoAnterior.toISOString().split('T')[0])

      const calcularTotal = (data) => {
        return data?.reduce((sum, d) => {
          return sum + (d.delivery_lines?.reduce((s, l) => 
            s + (l.cantidad_entregada * l.precio_unit), 0) || 0)
        }, 0) || 0
      }

      const ventasSemanaAnt = calcularTotal(semanaAnt)
      const ventasMesAnt = calcularTotal(mesAnt)
      const ventasAñoAnt = calcularTotal(añoAnt)

      setComparativas({
        semana: {
          actual: ventasActuales,
          anterior: ventasSemanaAnt,
          cambio: ventasSemanaAnt > 0 ? (((ventasActuales - ventasSemanaAnt) / ventasSemanaAnt) * 100).toFixed(1) : 0
        },
        mes: {
          actual: ventasActuales,
          anterior: ventasMesAnt,
          cambio: ventasMesAnt > 0 ? (((ventasActuales - ventasMesAnt) / ventasMesAnt) * 100).toFixed(1) : 0
        },
        año: {
          actual: ventasActuales,
          anterior: ventasAñoAnt,
          cambio: ventasAñoAnt > 0 ? (((ventasActuales - ventasAñoAnt) / ventasAñoAnt) * 100).toFixed(1) : 0
        }
      })
    } catch (error) {
      console.error('Error calculando comparativas:', error)
    }
  }

  function generarAnalisisSobrasPorCliente(deliveries) {
    const clienteProductoStats = {}
    
    deliveries?.forEach(delivery => {
      const clienteId = delivery.client_id
      const clienteNombre = delivery.clients?.alias || delivery.clients?.razon_social || 'Desconocido'
      
      if (!clienteProductoStats[clienteId]) {
        clienteProductoStats[clienteId] = {
          nombre: clienteNombre,
          productos: {}
        }
      }
      
      delivery.delivery_lines?.forEach(line => {
        const productoNombre = line.products?.nombre || 'Desconocido'
        const entregado = line.cantidad_entregada || 0
        const devuelto = line.cantidad_devuelta || 0
        
        if (!clienteProductoStats[clienteId].productos[productoNombre]) {
          clienteProductoStats[clienteId].productos[productoNombre] = {
            nombre: productoNombre,
            entregado: 0,
            devuelto: 0
          }
        }
        
        clienteProductoStats[clienteId].productos[productoNombre].entregado += entregado
        clienteProductoStats[clienteId].productos[productoNombre].devuelto += devuelto
      })
    })
    
    const sobras = []
    
    Object.values(clienteProductoStats).forEach(cliente => {
      const productosSobras = Object.values(cliente.productos)
        .map(p => ({
          ...p,
          porcentajeDev: p.entregado > 0 ? ((p.devuelto / p.entregado) * 100).toFixed(1) : 0
        }))
        .filter(p => parseFloat(p.porcentajeDev) > 15 && p.devuelto > 5)
        .sort((a, b) => parseFloat(b.porcentajeDev) - parseFloat(a.porcentajeDev))
      
      if (productosSobras.length > 0) {
        sobras.push({
          cliente: cliente.nombre,
          productos: productosSobras
        })
      }
    })
    
    setSobrasPorCliente(sobras.sort((a, b) => {
      const maxDevA = Math.max(...a.productos.map(p => parseFloat(p.porcentajeDev)))
      const maxDevB = Math.max(...b.productos.map(p => parseFloat(p.porcentajeDev)))
      return maxDevB - maxDevA
    }))
  }

  function generarAlertas(productos, diasSemana, ranking) {
    const alertasNuevas = []

    productos.forEach(p => {
      const porcentaje = parseFloat(p.porcentaje)
      if (porcentaje > 15) {
        alertasNuevas.push({
          tipo: 'critico',
          mensaje: `${p.nombre}: ${porcentaje}% devoluciones (${p.devuelto}/${p.entregado} ud)`
        })
      } else if (porcentaje > 10) {
        alertasNuevas.push({
          tipo: 'advertencia',
          mensaje: `${p.nombre}: ${porcentaje}% devoluciones`
        })
      }
    })

    diasSemana.forEach(dia => {
      const porcentaje = parseFloat(dia.porcentajeDev)
      if (porcentaje > 18) {
        alertasNuevas.push({
          tipo: 'advertencia',
          mensaje: `${dia.dia}: ${porcentaje}% devoluciones`
        })
      }
    })

    setAlertas(alertasNuevas.slice(0, 5))
  }

  function exportarAExcel() {
    try {
      const wb = XLSX.utils.book_new()
      
      const resumen = [
        ['INFORME DE VENTAS Y DEVOLUCIONES'],
        [],
        ['Período', periodo],
        ['Cliente', clienteSeleccionado === 'todos' ? 'Todos los clientes' : 
          clientes.find(c => c.id === clienteSeleccionado)?.alias],
        ['Fecha generación', new Date().toLocaleString('es-ES')],
        [],
        ['KPIs'],
        ['Ventas Brutas', kpis.ventasBrutas.toFixed(2) + '€'],
        ['Devoluciones', kpis.devoluciones.toFixed(2) + '€'],
        ['% Devolución', kpis.porcentajeDevolucion + '%'],
        ['Ventas Netas', kpis.ventasNetas.toFixed(2) + '€'],
        [],
        ['COMPARATIVAS'],
        ['Semana anterior', comparativas.semana.anterior.toFixed(2) + '€', 
         comparativas.semana.cambio + '%'],
        ['Mes anterior', comparativas.mes.anterior.toFixed(2) + '€', 
         comparativas.mes.cambio + '%'],
        ['Año anterior', comparativas.año.anterior.toFixed(2) + '€', 
         comparativas.año.cambio + '%']
      ]
      const wsResumen = XLSX.utils.aoa_to_sheet(resumen)
      XLSX.utils.book_append_sheet(wb, wsResumen, 'Resumen')
      
      const productosExport = datosProductos.map(p => ({
        'Producto': p.nombre,
        'Entregado': p.entregado,
        'Devuelto': p.devuelto,
        '% Devolución': p.porcentaje + '%'
      }))
      const wsProductos = XLSX.utils.json_to_sheet(productosExport)
      XLSX.utils.book_append_sheet(wb, wsProductos, 'Productos')
      
      const clientesExport = rankingClientes.map(c => ({
        'Cliente': c.nombre,
        'Ventas': c.ventas.toFixed(2),
        'Devoluciones': c.devoluciones.toFixed(2),
        '% Dev': c.porcentajeDev + '%'
      }))
      const wsClientes = XLSX.utils.json_to_sheet(clientesExport)
      XLSX.utils.book_append_sheet(wb, wsClientes, 'Ranking Clientes')
      
      const diasExport = datosDiaSemana.map(d => ({
        'Día': d.dia,
        'Entregas': d.entregas.toFixed(2),
        'Devoluciones': d.devoluciones.toFixed(2),
        'Neto': d.neto.toFixed(2),
        '% Dev': d.porcentajeDev + '%'
      }))
      const wsDias = XLSX.utils.json_to_sheet(diasExport)
      XLSX.utils.book_append_sheet(wb, wsDias, 'Por Día Semana')
      
      const sobrasExport = []
      sobrasPorCliente.forEach(c => {
        c.productos.forEach(p => {
          sobrasExport.push({
            'Cliente': c.cliente,
            'Producto': p.nombre,
            'Entregado': p.entregado,
            'Devuelto': p.devuelto,
            '% Dev': p.porcentajeDev + '%',
            'Sugerencia': `Reducir ${p.devuelto} ud`
          })
        })
      })
      if (sobrasExport.length > 0) {
        const wsSobras = XLSX.utils.json_to_sheet(sobrasExport)
        XLSX.utils.book_append_sheet(wb, wsSobras, 'Sobras por Cliente')
      }
      
      const nombreArchivo = `informe-${periodo}-${new Date().toISOString().split('T')[0]}.xlsx`
      XLSX.writeFile(wb, nombreArchivo)
      
      alert('✅ Informe exportado correctamente')
    } catch (error) {
      console.error('Error exportando:', error)
      alert('Error al exportar: ' + error.message)
    }
  }

  function abrirDetalleProducto(producto, clientes) {
    const hoy = new Date().toISOString().split('T')[0]
    setModalDetalle({
      producto: producto,
      fecha: hoy,
      datosClientes: clientes
    })
  }

  const clienteNombre = clienteSeleccionado === 'todos' 
    ? 'Todos los clientes'
    : clientes.find(c => c.id === clienteSeleccionado)?.alias || 'Cliente'

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>📊 Informes y Análisis</h1>
          <p>Análisis de ventas, devoluciones y tendencias</p>
        </div>
        <button onClick={exportarAExcel} className="btn-primary">
          <Download size={18} />
          Exportar Excel
        </button>
      </div>

      {loading ? (
        <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--muted)' }}>
          Procesando datos...
        </div>
      ) : (
        <>
          {/* ── 1. RESUMEN DEL DÍA ── */}
          <ResumenDelDia deliveries={deliveriesRaw} kpis={kpis} clientPricesMap={clientPricesMap} />

          {/* ── 2. TARJETA PERÍODO ── */}
          <div className="card" style={{ marginBottom: '1.5rem', overflow: 'hidden' }}>

            {/* Filtros */}
            <div style={{
              padding: '0.875rem 1.25rem',
              borderBottom: '1px solid var(--line)',
              display: 'flex',
              gap: '0.75rem',
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
              <select
                value={clienteSeleccionado}
                onChange={(e) => setClienteSeleccionado(e.target.value)}
                className="input-field"
                style={{ minWidth: '160px', flex: 1, maxWidth: '280px' }}
              >
                <option value="todos">📊 Todos los clientes</option>
                {clientes.map(c => (
                  <option key={c.id} value={c.id}>{c.alias || c.razon_social}</option>
                ))}
              </select>
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                {['semana', 'mes', 'año', 'max'].map(p => (
                  <button
                    key={p}
                    onClick={() => setPeriodo(p)}
                    className={periodo === p ? 'btn-primary' : 'btn-secondary'}
                    style={{ fontSize: '0.8rem', padding: '0.375rem 0.625rem' }}
                  >
                    {p === 'max' ? 'Máx' : p.charAt(0).toUpperCase() + p.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* KPIs row */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
              borderBottom: '1px solid var(--line)'
            }}>
              {[
                {
                  label: 'Ventas netas',
                  value: kpis.ventasNetas.toFixed(2) + '€',
                  sub: 'Brutas: ' + kpis.ventasBrutas.toFixed(2) + '€',
                  color: '#10b981'
                },
                {
                  label: 'Devoluciones',
                  value: kpis.devoluciones.toFixed(2) + '€',
                  sub: kpis.porcentajeDevolucion + '% del total',
                  color: 'var(--warn)'
                },
                {
                  label: 'vs Semana ant.',
                  value: (parseFloat(comparativas.semana.cambio) >= 0 ? '↗ +' : '↘ ') + comparativas.semana.cambio + '%',
                  sub: comparativas.semana.anterior.toFixed(0) + '€ anterior',
                  color: parseFloat(comparativas.semana.cambio) >= 0 ? '#10b981' : 'var(--warn)'
                },
                {
                  label: 'vs Mes ant.',
                  value: (parseFloat(comparativas.mes.cambio) >= 0 ? '↗ +' : '↘ ') + comparativas.mes.cambio + '%',
                  sub: comparativas.mes.anterior.toFixed(0) + '€ anterior',
                  color: parseFloat(comparativas.mes.cambio) >= 0 ? '#10b981' : 'var(--warn)'
                }
              ].map((kpi, i, arr) => (
                <div key={i} style={{
                  padding: '1rem 1.25rem',
                  borderRight: i < arr.length - 1 ? '1px solid var(--line)' : 'none'
                }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--muted)', marginBottom: '0.25rem' }}>
                    {kpi.label}
                  </div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: kpi.color, lineHeight: 1, marginBottom: '0.2rem' }}>
                    {kpi.value}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--muted)' }}>{kpi.sub}</div>
                </div>
              ))}
            </div>

            {/* Top devoluciones del período */}
            <div style={{ padding: '1rem 1.25rem' }}>
              <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--warn)', marginBottom: '0.6rem' }}>
                Más devoluciones en el período
              </div>
              {datosProductos.filter(p => p.devuelto > 0).length > 0 ? (
                datosProductos.filter(p => p.devuelto > 0).slice(0, 5).map((p, i) => {
                  const pct = parseFloat(p.porcentaje)
                  const col = pct > 15 ? 'var(--warn)' : pct > 8 ? '#f59e0b' : '#10b981'
                  return (
                    <div
                      key={i}
                      onClick={() => abrirDetalleProducto(p.nombre, p.clientes)}
                      style={{ marginBottom: '0.5rem', cursor: 'pointer' }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', marginBottom: '0.2rem' }}>
                        <span>{p.nombre}</span>
                        <span style={{ color: col, fontWeight: 700 }}>{p.porcentaje}% · −{p.devuelto} ud</span>
                      </div>
                      <div style={{ height: '3px', background: 'var(--line)', borderRadius: '2px' }}>
                        <div style={{ height: '100%', width: `${Math.min(pct * 4, 100)}%`, background: col, borderRadius: '2px' }} />
                      </div>
                    </div>
                  )
                })
              ) : (
                <div style={{ color: '#10b981', fontSize: '0.875rem' }}>✅ Sin devoluciones significativas en este período</div>
              )}
            </div>
          </div>

          {/* ── 3. GRÁFICOS LADO A LADO ── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            <div className="card" style={{ padding: '1.25rem' }}>
              <h3 style={{ marginBottom: '0.75rem', fontSize: '0.9375rem' }}>📈 Evolución — {clienteNombre}</h3>
              {datosEvolucion.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={datosEvolucion}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
                    <XAxis dataKey="fecha" stroke="var(--muted)" tick={{ fill: 'var(--muted)', fontSize: 10 }} />
                    <YAxis stroke="var(--muted)" tick={{ fill: 'var(--muted)', fontSize: 10 }} />
                    <Tooltip contentStyle={{ background: 'var(--bg-secondary)', border: '1px solid var(--line)', borderRadius: '6px', fontSize: '0.8rem' }} />
                    <Legend wrapperStyle={{ fontSize: '0.8rem' }} />
                    <Line type="monotone" dataKey="neto" stroke="#10b981" name="Neto" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="devoluciones" stroke="var(--warn)" name="Dev." strokeWidth={1.5} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--muted)', fontSize: '0.875rem' }}>Sin datos</div>
              )}
            </div>

            <div className="card" style={{ padding: '1.25rem' }}>
              <h3 style={{ marginBottom: '0.75rem', fontSize: '0.9375rem' }}>📅 Por día de semana</h3>
              {datosDiaSemana.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={datosDiaSemana}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
                    <XAxis dataKey="dia" stroke="var(--muted)" tick={{ fill: 'var(--muted)', fontSize: 10 }} />
                    <YAxis stroke="var(--muted)" tick={{ fill: 'var(--muted)', fontSize: 10 }} />
                    <Tooltip contentStyle={{ background: 'var(--bg-secondary)', border: '1px solid var(--line)', borderRadius: '6px', fontSize: '0.8rem' }} />
                    <Legend wrapperStyle={{ fontSize: '0.8rem' }} />
                    <Bar dataKey="neto" fill="#10b981" name="Neto" />
                    <Bar dataKey="devoluciones" fill="var(--warn)" name="Dev." />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--muted)', fontSize: '0.875rem' }}>Sin datos</div>
              )}
            </div>
          </div>

          {/* ── 4. TABLA PRODUCTOS + RANKING CLIENTES ── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            <div className="card" style={{ padding: '1.25rem' }}>
              <h3 style={{ marginBottom: '0.75rem', fontSize: '0.9375rem' }}>📦 Productos</h3>
              {datosProductos.length > 0 ? (
                <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                    <thead style={{ position: 'sticky', top: 0, background: 'var(--bg)' }}>
                      <tr>
                        <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left', color: 'var(--muted)', fontWeight: 600, borderBottom: '1px solid var(--line)' }}>Producto</th>
                        <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right', color: 'var(--muted)', fontWeight: 600, borderBottom: '1px solid var(--line)' }}>Ent.</th>
                        <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right', color: 'var(--muted)', fontWeight: 600, borderBottom: '1px solid var(--line)' }}>Dev.</th>
                        <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right', color: 'var(--muted)', fontWeight: 600, borderBottom: '1px solid var(--line)' }}>%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {datosProductos.map((p, idx) => {
                        const pct = parseFloat(p.porcentaje)
                        const col = pct > 15 ? 'var(--warn)' : pct > 8 ? '#f59e0b' : '#10b981'
                        return (
                          <tr key={idx} onClick={() => abrirDetalleProducto(p.nombre, p.clientes)} style={{ cursor: 'pointer' }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                            <td style={{ padding: '0.35rem 0.5rem', borderBottom: '1px solid var(--line)' }}>{p.nombre}</td>
                            <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right', borderBottom: '1px solid var(--line)' }}>{p.entregado}</td>
                            <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right', borderBottom: '1px solid var(--line)', color: p.devuelto > 0 ? 'var(--warn)' : 'var(--muted)' }}>{p.devuelto}</td>
                            <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right', borderBottom: '1px solid var(--line)', fontWeight: 700, color: col }}>{p.porcentaje}%</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--muted)', fontSize: '0.875rem' }}>Sin datos</div>
              )}
            </div>

            <div className="card" style={{ padding: '1.25rem' }}>
              <h3 style={{ marginBottom: '0.75rem', fontSize: '0.9375rem' }}>🏆 Ranking clientes</h3>
              {rankingClientes.length > 0 ? (
                <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                    <thead style={{ position: 'sticky', top: 0, background: 'var(--bg)' }}>
                      <tr>
                        <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left', color: 'var(--muted)', fontWeight: 600, borderBottom: '1px solid var(--line)', width: '2rem' }}>#</th>
                        <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left', color: 'var(--muted)', fontWeight: 600, borderBottom: '1px solid var(--line)' }}>Cliente</th>
                        <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right', color: 'var(--muted)', fontWeight: 600, borderBottom: '1px solid var(--line)' }}>Ventas</th>
                        <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right', color: 'var(--muted)', fontWeight: 600, borderBottom: '1px solid var(--line)' }}>%Dev</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rankingClientes.map((c, idx) => {
                        const pct = parseFloat(c.porcentajeDev)
                        const col = pct > 15 ? 'var(--warn)' : pct > 8 ? '#f59e0b' : '#10b981'
                        return (
                          <tr key={idx}>
                            <td style={{ padding: '0.35rem 0.5rem', borderBottom: '1px solid var(--line)', color: 'var(--muted)', fontSize: '0.75rem', fontWeight: 700 }}>{idx + 1}</td>
                            <td style={{ padding: '0.35rem 0.5rem', borderBottom: '1px solid var(--line)' }}>{c.nombre}</td>
                            <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right', borderBottom: '1px solid var(--line)', fontWeight: 700 }}>{c.ventas.toFixed(0)}€</td>
                            <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right', borderBottom: '1px solid var(--line)', fontWeight: 700, color: col }}>{c.porcentajeDev}%</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--muted)', fontSize: '0.875rem' }}>Sin datos</div>
              )}
            </div>
          </div>

          {/* ── 5. SOBRAS POR CLIENTE ── */}
          {sobrasPorCliente.length > 0 && (
            <div className="card" style={{ padding: '1.25rem', marginBottom: '1.5rem', borderLeft: '4px solid var(--warn)' }}>
              <h3 style={{ marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9375rem' }}>
                <AlertCircle size={18} color="var(--warn)" />
                Clientes con exceso de producto
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '0.75rem' }}>
                {sobrasPorCliente.slice(0, 8).map((cliente, idx) => (
                  <div key={idx} style={{ padding: '0.75rem', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                    <div style={{ fontWeight: 700, marginBottom: '0.4rem', fontSize: '0.9rem' }}>{cliente.cliente}</div>
                    {cliente.productos.map((producto, pidx) => (
                      <div key={pidx} style={{ fontSize: '0.8rem', color: 'var(--muted)', display: 'flex', justifyContent: 'space-between', padding: '0.1rem 0' }}>
                        <span>{producto.nombre}</span>
                        <span style={{ color: 'var(--warn)', fontWeight: 600 }}>{producto.porcentajeDev}% · −{producto.devuelto} ud</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── 6. PREDICCIÓN DIARIA ── */}
          <PrediccionDiaria deliveries={deliveriesRaw} />
        </>
      )}

      {/* Modal detalle producto */}
      {modalDetalle && (
        <DetalleProductoModal
          producto={modalDetalle.producto}
          fecha={modalDetalle.fecha}
          datosClientes={modalDetalle.datosClientes}
          onClose={() => setModalDetalle(null)}
        />
      )}
    </div>
  )
}